import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const METHODS = [
  {
    id: "upi",
    label: "UPI",
    sub: "GPay · PhonePe · Paytm",
    hint: "You'll be redirected to your UPI app to complete payment instantly.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2"/>
        <path d="M9 11h6M12 8v6"/>
        <circle cx="12" cy="18" r="0.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    id: "card",
    label: "Card",
    sub: "Debit / Credit",
    hint: "All major debit and credit cards accepted. 3-D Secure verified.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
        <circle cx="6" cy="15" r="1" fill="currentColor" stroke="none"/>
        <circle cx="10" cy="15" r="1" fill="currentColor" stroke="none"/>
      </svg>
    ),
  },
  {
    id: "nb",
    label: "Net Banking",
    sub: "All major banks",
    hint: "Select your bank in the next step. Works with 50+ Indian banks.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7M2 11l10-7 10 7"/>
      </svg>
    ),
  },
  {
    id: "cod",
    label: "Cash on Delivery",
    sub: "Pay on arrival",
    hint: "Our delivery partner will collect payment when your order arrives.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
  },
];

const STEPS = ["Cart", "Delivery", "Payment"];

function Payment({ cart = [], setCart = () => {} }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponValidating, setCouponValidating] = useState(false);
  const [activeMethod, setActiveMethod] = useState("upi");
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  const subtotal = cart.reduce((s, i) => s + (i.salePrice ?? i.price) * i.quantity, 0);
  const discount = couponData?.discountAmount || 0;
  const total = couponData?.finalAmount ?? subtotal;

  const handleApplyCoupon = async () => {
    if (!coupon.trim()) { setCouponError("Enter a coupon code."); return; }
    setCouponValidating(true);
    setCouponError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/coupons/validate`,
        { code: coupon.trim().toUpperCase(), orderAmount: subtotal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCouponData(res.data);
      setCouponApplied(true);
    } catch (err) {
      const msg = err.response?.data;
      setCouponError(typeof msg === "string" && msg ? msg : "Invalid coupon code.");
      setCouponApplied(false);
      setCouponData(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon("");
    setCouponApplied(false);
    setCouponData(null);
    setCouponError("");
  };

  const handlePayment = async () => {
    if (activeMethod === "cod") {
      setProcessing(true);
      setPayError("");
      try {
        const res = await axios.post(
          `${API_BASE_URL}/payment/cod`,
          {
            items: cart.map(item => ({ product: { id: item.id }, quantity: item.quantity })),
            ...(couponApplied && coupon.trim() ? { couponCode: coupon.trim().toUpperCase() } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        localStorage.removeItem("cart");
        setCart([]);
        navigate("/order-success", {
          state: { isCOD: true, items: cart, total, method: "cod", orderId: res.data.id },
          replace: true,
        });
      } catch {
        setPayError("Could not place COD order. Please try again.");
        setProcessing(false);
      }
      return;
    }
    setProcessing(true);
    setPayError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/payment/create-order`,
        {
          items: cart.map(item => ({ product: { id: item.id }, quantity: item.quantity })),
          ...(couponApplied && coupon.trim() ? { couponCode: coupon.trim().toUpperCase() } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      const options = {
        key: data.key,
        amount: data.amount,
        currency: data.currency,
        order_id: data.id,
        name: "TableCure",
        image: window.location.origin + "/logo.png",
        description: "Ergonomic Desk Products",
        theme: { color: "#10b981" },
        handler: async (response) => {
          await axios.post(`${API_BASE_URL}/payment/verify`, null, {
            params: {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            },
            headers: { Authorization: `Bearer ${token}` },
          });
          localStorage.removeItem("cart");
          setCart([]);
          navigate("/order-success", {
            state: {
              items: cart,
              total,
              method: activeMethod,
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
            },
            replace: true,
          });
        },
        modal: { ondismiss: () => setProcessing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setPayError("Payment failed. Please try again.");
        setProcessing(false);
      });
      rzp.open();
    } catch {
      setPayError("Could not initiate payment. Please try again.");
      setProcessing(false);
    }
  };

  const activeMethodData = METHODS.find(m => m.id === activeMethod);

  return (
    <div className="tc-page min-h-screen" style={{ animation: "pmPageIn 0.4s ease both" }}>
      <style>{`
        @keyframes pmPageIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pmCardIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pmHintIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pmGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
          50%       { box-shadow: 0 0 0 7px rgba(16,185,129,0); }
        }
        .pm-method-card { transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease; cursor: pointer; }
        .pm-method-card:hover { transform: translateY(-1px); }
        .pm-pay-btn { transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease; }
        .pm-pay-btn:not(:disabled):hover {
          background: var(--accent-hover) !important;
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(16,185,129,0.28);
        }
        .pm-pay-btn:not(:disabled):active { transform: translateY(0); }
        .pm-pay-btn:not(:disabled) { animation: pmGlow 2.8s ease-in-out infinite; }
        .pm-back-btn { transition: color 0.15s ease, border-color 0.15s ease; }
        .pm-back-btn:hover { color: var(--text-primary) !important; border-color: var(--border-default) !important; }
        .pm-coupon-remove { transition: color 0.15s ease; }
        .pm-coupon-remove:hover { color: var(--danger-text) !important; }
      `}</style>

      <div className="tc-container" style={{ maxWidth: 960 }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 36,
          animation: "pmCardIn 0.35s ease both",
        }}>
          <div>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(32px, 5vw, 48px)",
              letterSpacing: "0.04em",
              color: "var(--text-primary)",
              lineHeight: 1,
              marginBottom: 6,
            }}>
              PAYMENT
            </h1>
            <p style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Complete your order securely
            </p>
          </div>

          {/* Step progress */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingTop: 4 }}>
            {STEPS.map((step, i) => (
              <div key={step} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    fontWeight: 500,
                    background: i === 2 ? "var(--accent)" : i < 2 ? "var(--accent-bg)" : "transparent",
                    border: `1px solid ${i < 2 ? "var(--accent)" : i === 2 ? "var(--accent)" : "var(--border-subtle)"}`,
                    color: i === 2 ? "var(--bg-primary)" : "var(--accent)",
                  }}>
                    {i < 2 ? "✓" : "3"}
                  </div>
                  <span style={{
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: i === 2 ? "var(--accent)" : "var(--text-muted)",
                    display: "none",
                  }}
                    className="sm:inline"
                  >
                    {step}
                  </span>
                </div>
                {i < 2 && (
                  <div style={{ width: 28, height: 1, background: "var(--border-subtle)" }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>

          {/* ── Left column ── */}
          <div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Payment method */}
            <div className="tc-card" style={{ animation: "pmCardIn 0.38s ease 0.05s both" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}>
                <span style={{
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}>
                  Payment Method
                </span>
                <div style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--accent)",
                  boxShadow: "0 0 8px var(--accent)",
                }} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                {METHODS.map(m => {
                  const isActive = activeMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      className="pm-method-card"
                      onClick={() => setActiveMethod(m.id)}
                      style={{
                        padding: "14px 12px",
                        borderRadius: "var(--radius-md)",
                        border: `1px solid ${isActive ? "var(--accent)" : "var(--border-subtle)"}`,
                        background: isActive ? "var(--accent-bg)" : "transparent",
                        textAlign: "left",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <span style={{ color: isActive ? "var(--accent)" : "var(--text-muted)" }}>
                        {m.icon}
                      </span>
                      <div>
                        <p style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: isActive ? "var(--accent)" : "var(--text-primary)",
                          marginBottom: 2,
                        }}>
                          {m.label}
                        </p>
                        <p style={{
                          fontSize: 10,
                          color: isActive ? "var(--accent)" : "var(--text-muted)",
                          letterSpacing: "0.04em",
                          opacity: 0.8,
                        }}>
                          {m.sub}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Method hint */}
              {activeMethodData && (
                <div
                  key={activeMethod}
                  style={{
                    marginTop: 14,
                    padding: "10px 14px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--bg-secondary)",
                    borderLeft: "2px solid var(--accent)",
                    animation: "pmHintIn 0.2s ease both",
                  }}
                >
                  <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                    {activeMethod === "cod"
                      ? `Pay ₹${total.toLocaleString("en-IN")} in cash when your order arrives.`
                      : activeMethodData.hint}
                  </p>
                </div>
              )}
            </div>

            {/* Coupon */}
            <div className="tc-card" style={{ animation: "pmCardIn 0.38s ease 0.1s both" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}>
                <span style={{
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}>
                  Coupon / Promo
                </span>
                {couponApplied && couponData && (
                  <span style={{
                    fontSize: 9,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: "var(--radius-pill)",
                    background: "var(--success-bg)",
                    color: "var(--success-text)",
                    border: "1px solid var(--success-border)",
                    fontFamily: "var(--font-mono)",
                  }}>
                    {couponData.discountType === "PERCENTAGE"
                      ? `${couponData.discountValue}% off applied`
                      : `₹${couponData.discountValue} off applied`}
                  </span>
                )}
              </div>

              {couponApplied ? (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--success-bg)",
                  border: "1px solid var(--success-border)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{
                      fontSize: 12,
                      fontFamily: "var(--font-mono)",
                      color: "var(--success-text)",
                      letterSpacing: "0.12em",
                    }}>
                      {coupon.toUpperCase()}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--success-text)", opacity: 0.8 }}>
                      — ₹{discount.toLocaleString("en-IN")} saved
                    </span>
                  </div>
                  <button
                    className="pm-coupon-remove"
                    onClick={handleRemoveCoupon}
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--text-muted)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      className="tc-input"
                      placeholder="Enter coupon code"
                      value={coupon}
                      onChange={e => { setCoupon(e.target.value); setCouponError(""); }}
                      onKeyDown={e => e.key === "Enter" && !couponValidating && handleApplyCoupon()}
                      style={{
                        flex: 1,
                        textTransform: "uppercase",
                        fontFamily: "var(--font-mono)",
                        letterSpacing: "0.1em",
                        fontSize: 12,
                      }}
                    />
                    <button
                      className="tc-btn tc-btn-secondary"
                      onClick={handleApplyCoupon}
                      disabled={couponValidating}
                      style={{ padding: "0 18px", fontSize: 11, letterSpacing: "0.1em", flexShrink: 0, opacity: couponValidating ? 0.6 : 1 }}
                    >
                      {couponValidating ? "..." : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <p style={{ fontSize: 11, color: "var(--danger-text)", marginTop: 8 }}>
                      {couponError}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Security trust bar */}
            <div style={{
              animation: "pmCardIn 0.38s ease 0.15s both",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 24,
              padding: "14px 20px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-secondary)",
            }}>
              {[
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  ),
                  label: "Secure Payment",
                },
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                  ),
                  label: "Razorpay",
                },
                {
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  ),
                  label: "256-bit SSL",
                },
              ].map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "var(--accent)", opacity: 0.7 }}>{b.icon}</span>
                  <span style={{
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                  }}>
                    {b.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column: summary + pay ── */}
          <div style={{
            width: "100%",
            maxWidth: 300,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            animation: "pmCardIn 0.38s ease 0.08s both",
          }}>

            {/* Order summary card */}
            <div className="tc-card" style={{ padding: 0, overflow: "hidden" }}>
              {/* Card header */}
              <div style={{
                padding: "14px 20px",
                borderBottom: "1px solid var(--border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <span style={{
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}>
                  Order Summary
                </span>
                <span style={{
                  fontSize: 10,
                  fontFamily: "var(--font-mono)",
                  color: "var(--accent)",
                  background: "var(--accent-bg)",
                  border: "1px solid var(--accent)",
                  borderRadius: "var(--radius-pill)",
                  padding: "2px 8px",
                  letterSpacing: "0.06em",
                }}>
                  {cart.length} item{cart.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Items list */}
              <div style={{
                padding: "16px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                maxHeight: 200,
                overflowY: "auto",
              }}>
                {cart.length === 0 ? (
                  <p style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", padding: "8px 0" }}>
                    No items in cart
                  </p>
                ) : (
                  cart.map(item => (
                    <div key={item.id} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      {/* Color dot indicator */}
                      <div style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--accent)",
                        marginTop: 5,
                        flexShrink: 0,
                        opacity: 0.6,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          fontSize: 12,
                          color: "var(--text-primary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginBottom: 2,
                        }}>
                          {item.name}
                        </p>
                        <p style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                          qty {item.quantity}
                        </p>
                      </div>
                      <p style={{
                        fontSize: 12,
                        color: "var(--text-secondary)",
                        flexShrink: 0,
                        fontFamily: "var(--font-mono)",
                      }}>
                        ₹{((item.salePrice ?? item.price) * item.quantity).toLocaleString("en-IN")}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Price breakdown */}
              <div style={{
                padding: "14px 20px",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Subtotal</span>
                  <span style={{ fontSize: 11, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
                    ₹{subtotal.toLocaleString("en-IN")}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Shipping</span>
                  <span style={{ fontSize: 11, color: "var(--success-text)", fontFamily: "var(--font-mono)" }}>Free</span>
                </div>
                {couponApplied && discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: "var(--success-text)" }}>
                      Discount ({coupon.trim().toUpperCase()})
                    </span>
                    <span style={{ fontSize: 11, color: "var(--success-text)", fontFamily: "var(--font-mono)" }}>
                      −₹{discount.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div style={{
                padding: "16px 20px",
                borderTop: "1px solid var(--border-subtle)",
                background: "var(--bg-tertiary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <span style={{
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}>
                  Total
                </span>
                <span style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 36,
                  letterSpacing: "0.03em",
                  color: "var(--accent)",
                  lineHeight: 1,
                }}>
                  ₹{total.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* Error */}
            {payError && (
              <p style={{
                fontSize: 11,
                color: "var(--danger-text)",
                textAlign: "center",
                padding: "8px 12px",
                background: "var(--danger-bg)",
                border: "1px solid var(--danger-border)",
                borderRadius: "var(--radius-sm)",
              }}>
                {payError}
              </p>
            )}

            {/* Pay button */}
            <button
              className="pm-pay-btn"
              onClick={handlePayment}
              disabled={processing}
              style={{
                width: "100%",
                padding: "16px 24px",
                borderRadius: "var(--radius-md)",
                border: "none",
                background: "var(--accent)",
                color: "var(--bg-primary)",
                cursor: processing ? "not-allowed" : "pointer",
                opacity: processing ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {processing ? (
                <>
                  <span className="tc-spinner" style={{ width: 13, height: 13, borderWidth: 1.5, borderColor: "rgba(0,0,0,0.25)", borderTopColor: "var(--bg-primary)" }} />
                  <span style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "var(--font-mono)" }}>
                    Validating...
                  </span>
                </>
              ) : (
                <>
                  <span style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 20,
                    letterSpacing: "0.06em",
                  }}>
                    {activeMethod === "cod" ? "PLACE ORDER" : `PAY ₹${total.toLocaleString("en-IN")}`}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>

            {/* Back link */}
            <button
              className="pm-back-btn"
              onClick={() => navigate("/checkout")}
              style={{
                width: "100%",
                padding: "10px 24px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                background: "transparent",
                color: "var(--text-muted)",
                cursor: "pointer",
                fontSize: 10, 
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontFamily: "var(--font-mono)",
              }}
            >
              ← Back to Delivery
            </button>

            {/* Fine print */}
            <p style={{
              fontSize: 10,
              color: "var(--text-muted)",
              textAlign: "center",
              lineHeight: 1.7,
              letterSpacing: "0.02em",
            }}>
              By placing this order you agree to our{" "}
              <span style={{ color: "var(--accent)", cursor: "pointer" }}>Terms of Service</span>
              {" "}and{" "}
              <span style={{ color: "var(--accent)", cursor: "pointer" }}>Privacy Policy</span>.
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Payment;
